/**
 * ForgotPasswordDialog Component
 *
 * Permet à un admin de demander un lien de réinitialisation de mot de passe.
 * Le backend envoie l'email via Firebase — la réponse est toujours générique.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { LockReset as LockResetIcon } from '@mui/icons-material';
import AuthApiService from '../../services/authApi';
import { logger } from '../../lib/logger';

interface ForgotPasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (loading) return;
    // Réinitialiser les états à la fermeture
    setEmail('');
    setLoading(false);
    setSent(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await AuthApiService.adminForgotPassword(email.trim());
      setSent(true);
    } catch (err: unknown) {
      logger.error('Erreur forgot password:', err);
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LockResetIcon color="primary" />
          <Typography variant="h6">Mot de passe oublié</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {!sent ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Entrez votre adresse email. Si elle est associée à un compte administrateur,
                vous recevrez un lien pour réinitialiser votre mot de passe.
              </Typography>

              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <TextField
                label="Adresse email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                autoFocus
                autoComplete="email"
                disabled={loading}
              />
            </>
          ) : (
            <Alert severity="success">
              Si cet email est associé à un compte, un lien de réinitialisation a été envoyé.
              Vérifiez votre boîte de réception (et les spams).
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {!sent ? (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !email.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : undefined}
            >
              Envoyer le lien
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">
            Fermer
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
